import {
  hasAttendanceAccess,
  hasMlAgentCommissionAccess,
  hasExternalCommissionAccess,
  hasPaidAmountSettlementAccess,
  isContractPartyAdmin,
  hasShipmentCrossingAccess,
  hasOgclTransferRequestMenuAccess,
  hasLocationHubBookingAccess,
} from './networkMode.js';
import { isRoleModuleEnabled, sidebarItemModule } from './roleModuleAccess.js';

/** Accountant is finance-only: strict allow-list of top-level sidebar tabs. */
export const ACCOUNTANT_ALLOWED_ITEM_IDS = new Set([
  'dashboard',
  'shipments',
  'masterLoadingSheet',
  'masterLoadingSheetExternal',
  'shipmentCrossing',
  'updateStatus',
  'trackShipment',
  'cashbook',
  'settlements',
  'expenseManagement',
  'hrWorkforce',
  'misReports',
  'analyticalReports',
  'creditClients',
]);

/** First key of a jump chord. Second key opens a page. */
export const NAV_GO_PREFIX = 'a';

export function formatNavGoChord(goKey) {
  if (!goKey) return '';
  const prefix = NAV_GO_PREFIX.toUpperCase();
  if (goKey === '.') return `${prefix} then .`;
  if (goKey === ' ') return `${prefix} then Space`;
  return `${prefix} then ${String(goKey).toUpperCase()}`;
}
export const NAV_GO_KEYS = {
  dashboard: 'h',
  companyManagement: 'c',
  shipments: 's',
  masterLoadingSheet: 'm',
  masterLoadingSheetExternal: 'm',
  shipmentCrossing: 'k',
  updateStatus: 'u',
  trackShipment: 't',
  cashbook: 'b',
  externalMLSheet: 'l',
  settlements: 'e',
  locationHubVehicle: 'o',
  shipmentCommission: 'i',
  userManagement: 'p',
  hrWorkforce: 'a',
  expenseManagement: 'x',
  misReports: 'r',
  analyticalReports: 'n',
  onContract: 'q',
  contractManagement: 'y',
  agentNetwork: 'w',
  myPartnerships: 'z',
  contentManagement: 'j',
  agentCommission: 'e',
  companySettings: '.',
};

export function buildSidebarMenuItems(user) {
  if (!user) return [];

  if (user.role === 'Owner') {
    return [
      {
        id: 'dashboard',
        labelKey: 'sidebar.dashboard',
        path: '/owner-dashboard',
        visible: true,
        type: 'single',
      },
      {
        id: 'misReports',
        labelKey: 'sidebar.misReports',
        path: '/reports',
        visible: true,
        type: 'single',
      },
      {
        id: 'analyticalReports',
        labelKey: 'sidebar.analyticalReports',
        path: '/analytics',
        visible: true,
        type: 'single',
      },
      {
        id: 'expenseManagement',
        labelKey: 'sidebar.expenseManagement',
        path: '/expenses',
        visible: true,
        type: 'single',
      },
      {
        id: 'companySettings',
        labelKey: 'sidebar.companySettings',
        path: '/company-settings',
        visible: true,
        type: 'single',
      },
    ];
  }

  if (isContractPartyAdmin(user)) {
    return [
      {
        id: 'dashboard',
        labelKey: 'sidebar.dashboard',
        path: '/contract-party-dashboard',
        visible: true,
        type: 'single',
      },
      {
        id: 'masterLoadingSheet',
        labelKey: 'sidebar.masterLoadingSheet',
        path: '/ml-sheets',
        visible: true,
        type: 'single',
      },
      {
        id: 'agentCommission',
        labelKey: 'sidebar.agentCommissionPayments',
        path: '/ml-commission-payments',
        visible: true,
        type: 'single',
      },
      {
        id: 'userManagement',
        labelKey: 'sidebar.userManagement',
        path: '/users',
        visible: true,
        type: 'single',
      },
    ];
  }

  const isSystemAdmin = user.role === 'System Admin';
  const isAccountant = user.role === 'Accountant';
  const isHubAdmin = user.role === 'Hub Admin';
  const isAdminOrAccountant = ['System Admin', 'Admin', 'Accountant'].includes(user.role);
  const isOpsAdmin = isAdminOrAccountant || isHubAdmin;
  const isCompanyAgent = user.role === 'Agent' && !user.party_id;
  const shipmentRoles = ['System Admin', 'Admin', 'Accountant', 'Agent', 'Hub Admin'];

  const companyAccess = user.company_features || user.company_can_access || {};

  const hasShipmentAccess = isSystemAdmin || companyAccess.shipment || false;
  const hasExpensesAccess = isSystemAdmin || companyAccess.expenses || false;
  const hasLoansAccess = isSystemAdmin || companyAccess.loans || false;
  const hasCommissionAccess = isSystemAdmin || companyAccess.commission || false;
  const isExternalNetwork = user.network_mode === 'EXTERNAL';
  const showMlAgentCommission = hasMlAgentCommissionAccess(user);
  const showExternalCommission = hasExternalCommissionAccess(user);
  const showPaidAmountSettlement = hasPaidAmountSettlementAccess(user);
  const showOgclTransferRequests = hasOgclTransferRequestMenuAccess(user);
  const hubBookingAllowed = hasLocationHubBookingAccess(user);
  const externalDashboardPath = '/external-commission-dashboard';
  const hasExternalOnContract = isSystemAdmin || companyAccess.external_on_contract || false;
  const hasOnContract = isSystemAdmin || companyAccess.on_contract || false;
  const hasVehicleOwnerAccess = hasCommissionAccess || hasOnContract
    || (isExternalNetwork && (companyAccess.external_commission || companyAccess.commission))
    || hasExternalOnContract;
  const hasLocationHubVehicleMenu =
    isOpsAdmin && (hasShipmentAccess || hasVehicleOwnerAccess);
  const hasOnContractTruck = isSystemAdmin || companyAccess.on_contract_truck || false;
  const hasCashbookAccess = isSystemAdmin || companyAccess.cashbook || false;
  const hasAnalyticalReports = isSystemAdmin || companyAccess.analytical_reports !== false;
  const hasAgentNetwork = isSystemAdmin || (companyAccess.agent_network && user.network_mode === 'EXTERNAL');
  const hasMyPartnerships = Boolean(companyAccess.my_partnerships);
  const hasExternalML = Boolean(companyAccess.external_ml);
  const hasAttendanceAccessFlag = hasAttendanceAccess(user);
  const showExpenses = hasExpensesAccess && (isOpsAdmin || isCompanyAgent);
  const showLoans = hasLoansAccess && isAdminOrAccountant;
  const isAgent = user.role === 'Agent';
  const canSubmitCompanySettlement = user.can_submit_company_settlement === true;
  const showAgentPaidSettlement = isAgent && canSubmitCompanySettlement && hasShipmentAccess && showPaidAmountSettlement && hubBookingAllowed;
  const showAgentReturnToCompany = isAgent && canSubmitCompanySettlement && hasShipmentAccess && !isExternalNetwork;
  const showAgentSettlements = showAgentPaidSettlement || showAgentReturnToCompany;
  const showDispatchOgcl = hasShipmentAccess && showOgclTransferRequests && !isExternalNetwork;
  const showOpsSettlements = isOpsAdmin && hasShipmentAccess && (
    (showPaidAmountSettlement && hubBookingAllowed)
    || !isExternalNetwork
  );

  const items = [
    {
      id: 'dashboard',
      labelKey: 'sidebar.dashboard',
      path: user.role === 'Accountant' ? '/accountant-dashboard'
            : user.role === 'Agent' ? '/agent-dashboard'
            : user.role === 'Driver' ? '/driver-dashboard'
            : user.role === 'System Admin' ? '/system-admin-dashboard'
            : user.role === 'Contract Party Admin' ? '/contract-party-dashboard'
            : '/admin-dashboard',
      visible: true,
      type: 'single',
    },
    {
      id: 'companyManagement',
      labelKey: 'sidebar.companyManagement',
      visible: isSystemAdmin,
      type: 'dropdown',
      defaultPath: '/companies',
      children: [
        { labelKey: 'sidebar.companyProfile', path: '/companies', visible: isSystemAdmin },
        { labelKey: 'sidebar.companySubscription', path: '/company-subscriptions', visible: isSystemAdmin },
        { labelKey: 'sidebar.whatsappCounter', path: '/company-whatsapp-counters', visible: isSystemAdmin },
        { labelKey: 'sidebar.gstVerificationCounter', path: '/company-gst-verification-counters', visible: isSystemAdmin },
        { labelKey: 'sidebar.ewaybillCounter', path: '/company-ewaybill-counters', visible: isSystemAdmin },
      ],
    },
    {
      id: 'shipments',
      labelKey: 'sidebar.shipments',
      visible: hasShipmentAccess && shipmentRoles.includes(user.role),
      type: 'dropdown',
      defaultPath: hubBookingAllowed ? '/shipments' : '/missing-shipments',
      children: [
        { labelKey: 'sidebar.shipment', path: '/shipments', visible: hasShipmentAccess && shipmentRoles.includes(user.role) && hubBookingAllowed },
        { labelKey: 'sidebar.shipmentPayment', path: '/payments', visible: hasShipmentAccess && shipmentRoles.includes(user.role) && hubBookingAllowed },
        { labelKey: 'sidebar.shipmentMissing', path: '/missing-shipments', visible: hasShipmentAccess && shipmentRoles.includes(user.role) },
        { labelKey: 'sidebar.shipmentReturn', path: '/returns', visible: hasShipmentAccess && shipmentRoles.includes(user.role) },
      ],
    },
    {
      id: 'masterLoadingSheet',
      labelKey: 'sidebar.masterLoadingSheet',
      visible: hasShipmentAccess && shipmentRoles.includes(user.role) && !isExternalNetwork,
      ...(showDispatchOgcl
        ? {
            type: 'dropdown',
            defaultPath: '/ml-sheets',
            children: [
              {
                labelKey: 'sidebar.mlSheetsList',
                path: '/ml-sheets',
                visible: hasShipmentAccess && shipmentRoles.includes(user.role),
              },
              {
                labelKey: 'sidebar.ogclTransferRequests',
                path: '/ogcl-transfer-requests',
                visible: true,
              },
            ],
          }
        : {
            path: '/ml-sheets',
            type: 'single',
          }),
    },
    {
      id: 'masterLoadingSheetExternal',
      labelKey: 'sidebar.masterLoadingSheet',
      path: '/ml-sheets',
      visible: hasShipmentAccess && shipmentRoles.includes(user.role) && isExternalNetwork,
      type: 'single',
    },
    {
      id: 'shipmentCrossing',
      labelKey: 'sidebar.shipmentCrossing',
      path: '/shipment-crossing',
      visible: hasShipmentAccess && hasShipmentCrossingAccess(user),
      type: 'single',
    },
    {
      id: 'updateStatus',
      labelKey: 'sidebar.updateStatus',
      path: '/update-status',
      visible: hasShipmentAccess
        && ['System Admin', 'Admin', 'Accountant', 'Agent', 'Hub Admin', 'Contract Party Admin'].includes(user.role)
        && (user.role !== 'Agent' || user.location_hub_allow_delivery !== false),
      type: 'single',
    },
    {
      id: 'trackShipment',
      labelKey: 'sidebar.trackShipment',
      path: '/user/track-shipment',
      visible: hasShipmentAccess && ['System Admin', 'Admin', 'Accountant', 'Agent', 'Driver', 'Hub Admin'].includes(user.role),
      type: 'single',
    },
    {
      id: 'cashbook',
      labelKey: 'sidebar.cashbook',
      path: '/cashbook',
      visible: hasCashbookAccess && (isOpsAdmin || isAgent),
      type: 'single',
    },
    {
      id: 'externalMLSheet',
      labelKey: 'sidebar.externalMLSheet',
      path: '/external-ml',
      visible: hasShipmentAccess && isOpsAdmin && hasExternalML,
      type: 'single',
    },
    {
      id: 'settlements',
      labelKey: 'sidebar.settlements',
      visible: isAdminOrAccountant && showOpsSettlements,
      type: 'dropdown',
      defaultPath: '/company-settlement-approvals',
      children: [
        {
          labelKey: 'sidebar.settlementApprovals',
          path: '/company-settlement-approvals',
          visible: true,
        },
        {
          labelKey: 'sidebar.recordAgentSettlement',
          path: '/record-agent-settlement',
          visible: true,
        },
      ],
    },
    {
      id: 'hubSettlementApprovals',
      labelKey: 'sidebar.settlementApprovals',
      path: '/company-settlement-approvals',
      visible: isHubAdmin && showOpsSettlements,
      type: 'single',
    },
    {
      id: 'agentSettleWithCompany',
      labelKey: 'sidebar.settleWithCompany',
      path: '/company-settlements',
      visible: showAgentSettlements && !isAdminOrAccountant && !isHubAdmin,
      type: 'single',
    },
    {
      id: 'locationHubVehicle',
      labelKey: 'sidebar.locationHubVehicle',
      visible: hasLocationHubVehicleMenu,
      type: 'dropdown',
      defaultPath: (hasShipmentAccess && isOpsAdmin) ? '/location-hubs' : '/vehicle-owners',
      children: [
        { labelKey: 'sidebar.locationHubs', path: '/location-hubs', visible: hasShipmentAccess && isOpsAdmin },
        { labelKey: 'sidebar.vehicle', path: '/vehicles', visible: hasShipmentAccess && isOpsAdmin },
        { labelKey: 'sidebar.vehicleOwner', path: '/vehicle-owners', visible: hasVehicleOwnerAccess && isAdminOrAccountant },
        { labelKey: 'sidebar.routes', path: '/routes', visible: hasShipmentAccess && isOpsAdmin },
      ],
    },
    {
      id: 'shipmentCommission',
      labelKey: 'sidebar.shipmentCommission',
      visible: showExternalCommission && isAdminOrAccountant,
      type: 'dropdown',
      defaultPath: '/external-commissions',
      children: [
        {
          labelKey: 'sidebar.commissionDashboard',
          path: externalDashboardPath,
          visible: showExternalCommission && isAdminOrAccountant,
        },
        { labelKey: 'sidebar.commissionRecord', path: '/external-commissions', visible: showExternalCommission && isAdminOrAccountant },
        { labelKey: 'sidebar.agentBulkSettlement', path: '/external-commissions/bulk-settlement', visible: showExternalCommission && isAdminOrAccountant },
        {
          labelKey: 'sidebar.hubInsights',
          path: '/external-commissions/hub-insights',
          visible: showExternalCommission && ['System Admin', 'Admin', 'Accountant'].includes(user.role),
        },
      ],
    },
    {
      id: 'creditClients',
      labelKey: 'sidebar.clientsVendors',
      path: '/shipment-clients',
      visible: hasShipmentAccess && ['System Admin', 'Admin', 'Accountant'].includes(user.role),
      type: 'single',
    },
    {
      id: 'userManagement',
      labelKey: 'sidebar.userManagement',
      visible: (hasShipmentAccess || hasAttendanceAccessFlag || isOpsAdmin) && shipmentRoles.includes(user.role),
      type: 'dropdown',
      defaultPath: '/users',
      children: [
        { labelKey: 'sidebar.users', path: '/users', visible: shipmentRoles.includes(user.role) },
      ],
    },
    {
      id: 'hrWorkforce',
      labelKey: 'sidebar.hrWorkforce',
      visible: hasAttendanceAccessFlag && shipmentRoles.includes(user.role),
      type: 'dropdown',
      defaultPath: '/attendance',
      children: [
        { labelKey: 'sidebar.attendance', path: '/attendance', visible: hasAttendanceAccessFlag && shipmentRoles.includes(user.role) },
        { labelKey: 'sidebar.bulkLoginLogout', path: '/attendance/bulk-login', visible: hasAttendanceAccessFlag && ['System Admin', 'Admin', 'Accountant'].includes(user.role) },
        { labelKey: 'sidebar.salaryPayroll', path: '/salaries', visible: hasAttendanceAccessFlag && shipmentRoles.includes(user.role) },
      ],
    },
    ...(showExpenses && !showLoans
      ? [{
          id: 'expenseManagement',
          labelKey: 'sidebar.expenseManagement',
          path: '/expenses',
          visible: true,
          type: 'single',
        }]
      : [{
          id: 'expenseManagement',
          labelKey: 'sidebar.expenseManagement',
          visible: showExpenses || showLoans,
          type: 'dropdown',
          defaultPath: showExpenses ? '/expenses' : '/loans-given',
          children: [
            { labelKey: 'sidebar.expenses', path: '/expenses', visible: showExpenses },
            { labelKey: 'sidebar.loans', path: '/loans-given', visible: showLoans },
            { labelKey: 'sidebar.loansReceived', path: '/loans-received', visible: showLoans },
          ],
        }]),
    {
      id: 'onContract',
      labelKey: 'sidebar.onContract',
      path: '/on-contracts',
      visible: hasOnContract && isAdminOrAccountant,
      type: 'single',
    },
    {
      id: 'contractManagement',
      labelKey: 'sidebar.contractManagement',
      visible: hasOnContractTruck && isAdminOrAccountant,
      type: 'dropdown',
      defaultPath: '/contract-parties',
      children: [
        { labelKey: 'sidebar.contractParty', path: '/contract-parties', visible: hasOnContractTruck && isAdminOrAccountant },
        { labelKey: 'sidebar.vehicleContract', path: '/vehicle-contracts', visible: hasOnContractTruck && isAdminOrAccountant },
      ],
    },
    {
      id: 'agentNetwork',
      labelKey: 'sidebar.agentNetwork',
      visible: hasAgentNetwork && isAdminOrAccountant,
      type: 'dropdown',
      defaultPath: '/partner-agents',
      children: [
        { labelKey: 'sidebar.partnerAgents', path: '/partner-agents', visible: hasAgentNetwork && isAdminOrAccountant },
        { labelKey: 'sidebar.findAgent', path: '/partner-agents/find', visible: hasAgentNetwork && isAdminOrAccountant },
        { labelKey: 'sidebar.requests', path: '/partner-agents/requests', visible: hasAgentNetwork && isAdminOrAccountant },
      ],
    },
    {
      id: 'myPartnerships',
      labelKey: 'sidebar.myPartnerships',
      path: '/my-partnerships',
      visible: user.role === 'Agent' && hasMyPartnerships,
      type: 'single',
    },
    {
      id: 'contentManagement',
      labelKey: 'sidebar.contentManagement',
      visible: isSystemAdmin,
      type: 'dropdown',
      defaultPath: '/testimonials',
      children: [
        { labelKey: 'sidebar.testimonials', path: '/testimonials', visible: isSystemAdmin },
        { labelKey: 'sidebar.contactMessages', path: '/contact', visible: isSystemAdmin },
      ],
    },
    {
      id: 'misReports',
      labelKey: 'sidebar.misReports',
      path: '/reports',
      visible: (isOpsAdmin || isAgent) && (
        hasShipmentAccess
        || hasExpensesAccess
        || hasAttendanceAccessFlag
        || hasOnContract
        || hasOnContractTruck
        || showMlAgentCommission
        || hasCommissionAccess
        || showExternalCommission
      ),
      type: 'single',
    },
    {
      id: 'analyticalReports',
      labelKey: 'sidebar.analyticalReports',
      path: '/analytics',
      visible: (isOpsAdmin || user.role === 'Agent') && hasAnalyticalReports && (isSystemAdmin || hasShipmentAccess || hasCommissionAccess || hasExpensesAccess || hasOnContract || hasOnContractTruck),
      type: 'single',
    },
  ];

  const visibleItems = items
    .map((item) => {
      const moduleKey = sidebarItemModule(item.id);
      if (moduleKey && !isRoleModuleEnabled(user, moduleKey)) {
        return { ...item, visible: false };
      }
      return item;
    })
    .filter((item) => item.visible);

  if (isAccountant) {
    return visibleItems
      .filter((item) => ACCOUNTANT_ALLOWED_ITEM_IDS.has(item.id))
      .map((item) => {
        if (item.id !== 'expenseManagement' || item.type !== 'dropdown') return item;
        const expensesChild = (item.children || []).find(
          (child) => child.path === '/expenses' && child.visible,
        );
        if (!expensesChild) {
          return showExpenses
            ? {
                id: item.id,
                labelKey: item.labelKey,
                path: '/expenses',
                visible: true,
                type: 'single',
              }
            : null;
        }
        return {
          id: item.id,
          labelKey: item.labelKey,
          path: '/expenses',
          visible: true,
          type: 'single',
        };
      })
      .filter(Boolean);
  }

  return visibleItems;
}

export function flattenNavDestinations(user) {
  const menuItems = buildSidebarMenuItems(user);
  const rows = [];
  const seen = new Set();

  const push = (row) => {
    if (!row?.path) return;
    const key = `${row.path}::${row.labelKey || row.label || ''}`;
    if (seen.has(key)) return;
    seen.add(key);
    rows.push(row);
  };

  menuItems.forEach((item) => {
    const goKey = NAV_GO_KEYS[item.id];
    if (item.type === 'single' && item.path) {
      push({
        id: item.id,
        path: item.path,
        labelKey: item.labelKey,
        goKey,
        primary: true,
      });
      return;
    }
    if (item.type === 'dropdown') {
      const visibleChildren = (item.children || []).filter((child) => child.visible && child.path);
      const groupPath = item.defaultPath || visibleChildren[0]?.path;
      if (groupPath) {
        push({
          id: item.id,
          path: groupPath,
          labelKey: item.labelKey,
          goKey,
          primary: true,
        });
      }
      visibleChildren.forEach((child) => {
        push({
          id: `${item.id}:${child.path}`,
          path: child.path,
          labelKey: child.labelKey,
          parentLabelKey: item.labelKey,
          primary: false,
        });
      });
    }
  });

  if (['Admin', 'System Admin'].includes(user?.role)) {
    push({
      id: 'companySettings',
      path: '/company-settings',
      label: 'Company Settings',
      goKey: '.',
      primary: true,
    });
  }

  return rows;
}

export function navGoPathByKey(user) {
  const map = {};
  flattenNavDestinations(user)
    .filter((row) => row.primary && row.goKey)
    .forEach((row) => {
      map[row.goKey] = row.path;
    });
  return map;
}
